type ReaderFontOption = {
  value: string
  label: string
  /** CSS font-family（null は本の指定をそのまま使う） */
  family: string | null
  /** 端末にインストールされているか判定するフォント名。未指定なら常に表示（汎用スタック） */
  detect?: readonly string[]
}

const SERIF_FALLBACK = '"Hiragino Mincho ProN", "Yu Mincho", YuMincho, "Noto Serif JP", "Noto Serif CJK JP", serif'
const SANS_FALLBACK = '"Hiragino Sans", "Hiragino Kaku Gothic ProN", "Yu Gothic", YuGothic, "Noto Sans JP", sans-serif'

export const READER_FONT_OPTIONS = [
  { value: 'original', label: '本の設定', family: null },
  { value: 'mincho', label: '明朝', family: SERIF_FALLBACK },
  { value: 'gothic', label: 'ゴシック', family: `${SANS_FALLBACK}, Meiryo` },
  {
    value: 'maru-gothic',
    label: '丸ゴシック',
    family: `"Hiragino Maru Gothic ProN", "Hiragino Maru Gothic Pro", "Zen Maru Gothic", "M PLUS Rounded 1c", ${SANS_FALLBACK}`,
  },
  {
    value: 'toppan-mincho',
    label: '凸版文久明朝',
    family: `"Toppan Bunkyu Mincho", ${SERIF_FALLBACK}`,
    detect: ['Toppan Bunkyu Mincho'],
  },
  {
    value: 'toppan-gothic',
    label: '凸版文久ゴシック',
    family: `"Toppan Bunkyu Gothic", ${SANS_FALLBACK}`,
    detect: ['Toppan Bunkyu Gothic'],
  },
  {
    value: 'yu-mincho',
    label: '游明朝',
    family: `YuMincho, "Yu Mincho", ${SERIF_FALLBACK}`,
    detect: ['YuMincho', 'Yu Mincho'],
  },
  {
    value: 'yu-gothic',
    label: '游ゴシック',
    family: `YuGothic, "Yu Gothic", ${SANS_FALLBACK}`,
    detect: ['YuGothic', 'Yu Gothic'],
  },
  {
    value: 'biz-ud-mincho',
    label: 'BIZ UD明朝',
    family: `"BIZ UDPMincho", "BIZ UDMincho", ${SERIF_FALLBACK}`,
    detect: ['BIZ UDPMincho', 'BIZ UDMincho'],
  },
  {
    value: 'biz-ud-gothic',
    label: 'BIZ UDゴシック',
    family: `"BIZ UDPGothic", "BIZ UDGothic", ${SANS_FALLBACK}`,
    detect: ['BIZ UDPGothic', 'BIZ UDGothic'],
  },
  {
    value: 'tsukushi-a-maru',
    label: '筑紫A丸ゴシック',
    family: `"Tsukushi A Round Gothic", ${SANS_FALLBACK}`,
    detect: ['Tsukushi A Round Gothic'],
  },
  {
    value: 'tsukushi-b-maru',
    label: '筑紫B丸ゴシック',
    family: `"Tsukushi B Round Gothic", ${SANS_FALLBACK}`,
    detect: ['Tsukushi B Round Gothic'],
  },
  {
    value: 'yu-kyokasho',
    label: '游教科書体',
    family: `YuKyokasho, ${SERIF_FALLBACK}`,
    detect: ['YuKyokasho'],
  },
  {
    value: 'ud-kyokasho',
    label: 'UD教科書体',
    family: `"UD Digi Kyokasho NP-R", "UD Digi Kyokasho N-R", ${SERIF_FALLBACK}`,
    detect: ['UD Digi Kyokasho NP-R', 'UD Digi Kyokasho N-R'],
  },
  {
    value: 'klee',
    label: 'クレー',
    family: `Klee, "Klee One", ${SERIF_FALLBACK}`,
    detect: ['Klee', 'Klee One'],
  },
  {
    value: 'meiryo',
    label: 'メイリオ',
    family: `Meiryo, ${SANS_FALLBACK}`,
    detect: ['Meiryo'],
  },
] as const satisfies readonly ReaderFontOption[]

/** original: 本の指定フォントをそのまま使う */
export type ReaderFont = (typeof READER_FONT_OPTIONS)[number]['value']

export const READER_FONTS: readonly ReaderFont[] = READER_FONT_OPTIONS.map((option) => option.value)

const FONT_FAMILY_MAP = new Map<ReaderFont, string | null>(
  READER_FONT_OPTIONS.map((option) => [option.value, option.family]),
)

/** フォント設定 → CSS font-family（original は本の指定を尊重するため null） */
export const getFontFamilyCSS = (font: ReaderFont): string | null => FONT_FAMILY_MAP.get(font) ?? null

// --- インストール判定 ---

type MeasureContext = Pick<CanvasRenderingContext2D, 'font' | 'measureText'>

const BASE_FONTS = ['monospace', 'serif', 'sans-serif'] as const
// 和文は全角でどのフォントも幅が揃いやすいため、欧文を混ぜて差を出す
const TEST_TEXT = 'mmmmmmmmmmlli1WQ@#永あア漢字'

/**
 * 指定フォントが端末で使えるか判定する。
 * 存在しないフォントは基準フォントへフォールバックし幅が一致するため、
 * いずれかの基準フォントと幅が異なれば「使える」とみなす。
 */
export const isFontAvailable = (family: string, ctx: MeasureContext): boolean =>
  BASE_FONTS.some((base) => {
    ctx.font = `72px ${base}`
    const baseWidth = ctx.measureText(TEST_TEXT).width
    ctx.font = `72px "${family}", ${base}`
    return ctx.measureText(TEST_TEXT).width !== baseWidth
  })

export type ReaderFontChoice = { value: ReaderFont; label: string; family: string | null }

/** 端末で使えるフォントの選択肢を返す（ctx が無い環境では判定できないため全件） */
export const filterAvailableFonts = (ctx: MeasureContext | null): ReaderFontChoice[] =>
  READER_FONT_OPTIONS.filter((option) => {
    if (!ctx || !('detect' in option)) return true
    return option.detect.some((family) => isFontAvailable(family, ctx))
  }).map(({ value, label, family }) => ({ value, label, family }))
